import React from "react";
import * as _ from "underscore";
import Alert from "web-ui/components/internal/Alert";
import Button from "web-ui/components/internal/Button";
import { iconFactory } from "web-ui/components/internal/Icon";
import Modal from "web-ui/components/internal/Modal";
import Select from "web-ui/components/internal/Select";
import TextArea from "web-ui/components/internal/TextArea";
import TextInput from "web-ui/components/internal/TextInput";

import type { VoiceListVoice } from "../../api/generalTts";
import type {
  CreateVoicePayload,
  TtsGenerationAge,
  TtsGenerationCharacter,
  TtsGenerationGender,
  UpdateVoicePayload,
  VoicesV2Item,
} from "../../api/ttsGenerationBackend";
import {
  TTS_GENERATION_AGES,
  TTS_GENERATION_CHARACTERS,
  TTS_GENERATION_GENDERS,
  createVoice,
  patchVoice,
} from "../../api/ttsGenerationBackend";
import { useUser } from "../UserContextManager";

import styles from "./EditVoiceModal.module.scss";
import { formatCharacter, titleCaseFormatter } from "./VoiceFinder";

interface Props {
  mode: "clone" | "edit";
  onChange?: (mode: "clone" | "edit", voice: VoicesV2Item) => void;
  onRequestClose?: () => void;
  voice: VoiceListVoice;
}

const EditVoiceModal = ({ mode, onChange, onRequestClose, voice }: Props) => {
  const user = useUser();

  const [error, setError] = React.useState<string>();
  const [submitting, setSubmitting] = React.useState(false);

  const [age, setAge] = React.useState(voice.age ?? null);
  const [name, setName] = React.useState(
    mode === "clone" ? `${voice.name} (Copy)` : voice.name,
  );
  const [character, setCharacter] = React.useState(
    voice.world_character ?? null,
  );
  const [gender, setGender] = React.useState(voice.gender ?? null);
  const [notes, setNotes] = React.useState(voice.notes);
  const [pitch, setPitch] = React.useState(voice.provider_parameters.pitch);
  const [rate, setRate] = React.useState(voice.provider_parameters.rate);
  const [volume, setVolume] = React.useState(voice.provider_parameters.volume);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (voice.provider === "recorded") {
      throw Error("Cloning or editing recorded voices isn't supported");
    }
    const payload =
      mode === "clone"
        ? ((): CreateVoicePayload => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const payload = {
              ...(voice.getRawData() as VoicesV2Item),
              added_by: user?.username as string,
              age,
              gender,
              is_default_version: true as const,
              language_statuses: [],
              last_updated_by: user?.username as string,
              name,
              notes,
              version: 1 as const,
              world_character: character,
            };
            // @ts-expect-error Cleaning up the spread-in data
            delete payload.added_at;
            // @ts-expect-error Cleaning up the spread-in data
            delete payload.id;
            // @ts-expect-error Cleaning up the spread-in data
            delete payload.last_updated_at;
            if (pitch !== undefined) {
              payload.provider_parameters.pitch = isNaN(pitch ?? NaN)
                ? null
                : pitch;
            }
            if (rate !== undefined) {
              payload.provider_parameters.rate = isNaN(rate ?? NaN)
                ? null
                : rate;
            }
            if (volume !== undefined) {
              payload.provider_parameters.volume = isNaN(volume ?? NaN)
                ? null
                : volume;
            }
            return payload;
          })()
        : ((): UpdateVoicePayload => ({
            age,
            gender,
            last_updated_by: user?.username as string,
            name,
            notes,
            version: voice.version + 1,
            world_character: character,
          }))();
    setError(undefined);
    setSubmitting(true);
    try {
      const result = await (mode === "clone"
        ? createVoice(payload as CreateVoicePayload)
        : patchVoice(voice.id, payload));
      onChange?.(mode, result);
      onRequestClose?.();
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error(ex);
      setError(
        "Something went wrong or the name already exists; check the console and/or try again",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const paramInputs = [];

  if (rate !== undefined) {
    paramInputs.push(
      <TextInput
        labelText="Rate"
        onChange={e => setRate(parseFloat(e.value))}
        placeholder="Set rate (optional)"
        state={mode === "edit" ? "read-only" : "enabled"}
        type="number"
        value={`${rate ?? ""}`}
      />,
    );
  }

  if (pitch !== undefined) {
    paramInputs.push(
      <TextInput
        labelText="Pitch"
        onChange={e => setPitch(parseFloat(e.value))}
        placeholder="Set pitch (optional)"
        state={mode === "edit" ? "read-only" : "enabled"}
        type="number"
        value={`${pitch ?? ""}`}
      />,
    );
  }

  if (volume !== undefined) {
    paramInputs.push(
      <TextInput
        labelText="Volume"
        onChange={e => setVolume(parseFloat(e.value))}
        placeholder="Set volume (optional)"
        state={mode === "edit" ? "read-only" : "enabled"}
        type="number"
        value={`${volume ?? ""}`}
      />,
    );
  }

  return (
    <Modal
      footerBordered={false}
      footerLeft={
        mode === "clone" ? (
          <Button
            isGhost={true}
            onClick={onRequestClose}
            state={submitting ? "disabled" : "enabled"}
          >
            Cancel
          </Button>
        ) : null
      }
      footerRight={
        <Button
          onClick={() => handleSubmit()}
          state={submitting ? "loading" : "enabled"}
        >
          {mode === "clone" ? "Clone voice" : "Save changes"}
        </Button>
      }
      hasBackdrop={true}
      headerText={mode === "clone" ? "Clone voice" : "Edit voice"}
      onRequestClose={onRequestClose}
      open={true}
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        {error === undefined ? null : (
          <Alert
            color="red"
            description={error}
            leadingVisual={iconFactory("warning")}
          />
        )}
        <TextInput
          labelText="Voice name"
          onChange={e => setName(e.value)}
          value={name}
        />
        <div className={styles.two}>
          <Select
            labelText="Gender"
            onChange={e => setGender((e.value || null) as TtsGenerationGender)}
            value={gender ?? ""}
          >
            <option value="">Select gender</option>
            {TTS_GENERATION_GENDERS.map(value => (
              <option key={value} value={value}>
                {titleCaseFormatter(value)}
              </option>
            ))}
          </Select>
          <Select
            labelText="Character"
            onChange={e =>
              setCharacter((e.value || null) as TtsGenerationCharacter)
            }
            value={character ?? ""}
          >
            <option value="">Select character</option>
            {TTS_GENERATION_CHARACTERS.map(value => (
              <option key={value} value={value}>
                {formatCharacter(value)}
              </option>
            ))}
          </Select>
        </div>
        <div className={styles.two}>
          <Select
            labelText="Age"
            onChange={e => setAge((e.value || null) as TtsGenerationAge)}
            value={age ?? ""}
          >
            <option value="">Select age</option>
            {TTS_GENERATION_AGES.map(value => (
              <option key={value} value={value}>
                {titleCaseFormatter(value)}
              </option>
            ))}
          </Select>
        </div>
        <TextArea
          labelText="Notes"
          onChange={e => setNotes(e.value)}
          placeholder="Add notes about this voice (optional)"
          value={notes}
        />
        {paramInputs.length ? <hr className={styles.hr} /> : null}
        {_.chunk(paramInputs, 2).map((chunk, i) => (
          <div className={styles.two} key={i}>
            {chunk.map((el, j) => (
              <React.Fragment key={j}>{el}</React.Fragment>
            ))}
          </div>
        ))}
      </form>
    </Modal>
  );
};

export default EditVoiceModal;
